import {Button} from '@dropbox/dig-components/buttons';
import {
  DatePickerRange,
  type DatePickerRangeSelection,
  type DatePickerSelection,
} from '@dropbox/dig-components/date_picker';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowRightLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {
  putWorkstreamDraftPolicy,
  utcDayEndIso,
  utcDayStartIso,
  workstreamDraftPolicyQueryKey,
} from 'helpers/workstreamDraftPolicy';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useWorkstreamDraftPolicy} from 'hooks/useWorkstreamDraftPolicy';
import {useAtomValue} from 'jotai';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router';

const parsePolicyRange = (
  startIso: string | null | undefined,
  endIso: string | null | undefined
): DatePickerRangeSelection => {
  const start = startIso ? new Date(startIso) : null;
  const end = endIso ? new Date(endIso) : null;
  return [start, end];
};

export const AdminWorkstreamDraftPolicy = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const queryClient = useQueryClient();
  const [selectedRange, setSelectedRange] = useState<DatePickerRangeSelection>([null, null]);

  useDocumentTitle('Workstream draft policy');

  const {data} = useWorkstreamDraftPolicy();

  useEffect(() => {
    if (!data) {
      return;
    }
    setSelectedRange(parsePolicyRange(data.start_date ?? null, data.end_date ?? null));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const [start, end] = selectedRange;
      if (end == null) {
        return putWorkstreamDraftPolicy({start_date: null, end_date: null});
      }
      return putWorkstreamDraftPolicy({
        start_date: start != null ? utcDayStartIso(start) : null,
        end_date: utcDayEndIso(end),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: workstreamDraftPolicyQueryKey});
    },
  });

  const handleSelection = useCallback(
    ({startDate, endDate}: {startDate: DatePickerSelection; endDate: DatePickerSelection}) => {
      setSelectedRange([startDate, endDate]);
    },
    []
  );

  const handleChangeStart = useCallback((date: DatePickerSelection) => {
    setSelectedRange((prev) => [date ?? prev[0], prev[1]]);
  }, []);

  const handleChangeEnd = useCallback((date: DatePickerSelection) => {
    setSelectedRange((prev) => [prev[0], date ?? prev[1]]);
  }, []);

  const clearMutation = useMutation({
    mutationFn: () => putWorkstreamDraftPolicy({start_date: null, end_date: null}),
    onSuccess: async () => {
      setSelectedRange([null, null]);
      await queryClient.invalidateQueries({queryKey: workstreamDraftPolicyQueryKey});
    },
  });

  const policyActive = Boolean(data?.end_date);

  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Workstream draft policy', to: ROUTE_PATHS.ADMIN_WORKSTREAM_DRAFT_POLICY},
      ]}
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title size={24}>Workstream draft policy</Title>
        </Stack>

        <Box
          borderRadius="Large"
          borderWidth="1"
          borderStyle="Solid"
          borderColor="Border Subtle"
          padding="16"
          backgroundColor="Background Base"
        >
          <Stack gap="16">
            <Text>
              Set the date range for net-new milestones that may be edited without a change reason.
            </Text>
            <DatePickerRange selectedRange={selectedRange} onSelection={handleSelection}>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                style={{maxWidth: 420, gap: 8}}
              >
                <DatePickerRange.StartInput
                  size="small"
                  id="admin-workstream-draft-start"
                  value={selectedRange[0]}
                  onChange={handleChangeStart}
                  autocomplete="off"
                  placeholder="Start (optional)"
                />
                <UIIcon src={ArrowRightLine} />
                <DatePickerRange.EndInput
                  size="small"
                  id="admin-workstream-draft-end"
                  value={selectedRange[1]}
                  onChange={handleChangeEnd}
                  autocomplete="off"
                  placeholder="End"
                />
              </Box>
            </DatePickerRange>

            <Box display="flex" flexDirection="row" style={{gap: 12}}>
              <Button
                variant="primary"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || clearMutation.isPending}
              >
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => clearMutation.mutate()}
                disabled={saveMutation.isPending || clearMutation.isPending || !policyActive}
              >
                Clear policy
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Layout.Container>
  );
};
